import { CartAddEvent, CartUpdateEvent } from '@theme/events';

/**
 * MUCARIS cart upsells, quick view and free gift (snippets/mucaris-cart-upsells.liquid,
 * sections/mucaris-quick-view.liquid).
 *
 * - [data-upsell-step] scrolls the "Match with" row.
 * - [data-upsell-open="<product url>"] (the + on a card) opens the quick view: a side panel over the cart drawer
 *   with the product images, size buttons and add to cart. From the gift row ([data-gift]) it shows the price as
 *   free, the button says "Add for free", the line gets the hidden `_free_gift` property and the discount code
 *   from Theme settings (if any) is applied to the cart.
 * - [data-gift-remove="<line key>"] is set by Liquid when the cart no longer qualifies for the gift: the line is removed.
 *
 * Every change asks the Section Rendering API for all `cart-items-component` sections (drawer + cart page) and then
 * fires the theme's own cart events, so the drawer, the cart page and the cart bubble re-render like any other change.
 */

const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };
const QUICK_VIEW_SECTION = 'mucaris-quick-view';

/** @returns {string[]} */
function cartSectionIds() {
  const ids = new Set();
  for (const element of document.querySelectorAll('cart-items-component')) {
    if (element instanceof HTMLElement && element.dataset.sectionId) ids.add(element.dataset.sectionId);
  }
  return [...ids];
}

/**
 * @param {string} url
 * @param {object} body
 */
async function postJSON(url, body) {
  const response = await fetch(url, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return response.json();
}

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  return response.json();
}

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Adds the gift discount code to the codes already on the cart.
 * @param {string} code
 * @param {string[]} sections
 */
async function applyDiscountCode(code, sections) {
  try {
    const cart = await fetchCart();
    /** @type {string[]} */
    const existing = (cart.discount_codes || []).map((/** @type {{ code: string }} */ discount) => discount.code);
    if (existing.some((existingCode) => existingCode.toLowerCase() === code.toLowerCase())) return null;

    return await postJSON(Theme.routes.cart_update_url, {
      discount: [...existing, code].join(','),
      sections: sections.join(','),
      sections_url: window.location.pathname,
    });
  } catch (error) {
    console.error('[MUCARIS cart] Applying the free gift discount code failed:', error);
    return null;
  }
}

/**
 * Adds one variant to the cart and re-renders the cart through the theme's cart events.
 * @param {{ variantId: number, gift: boolean, giftProperty: string, giftCode: string }} options
 * @returns {Promise<string>} An error message, or '' when the item was added.
 */
async function addToCart({ variantId, gift, giftProperty, giftCode }) {
  /** @type {{ id: number, quantity: number, properties?: Record<string, string> }} */
  const item = { id: variantId, quantity: 1 };
  if (gift) item.properties = { _free_gift: giftProperty || 'Free gift' };

  const sections = cartSectionIds();
  const added = await postJSON(Theme.routes.cart_add_url, {
    items: [item],
    sections: sections.join(','),
    sections_url: window.location.pathname,
  });
  if (added.status) return added.description || added.message || 'Error';

  let renderedSections = added.sections;
  if (gift && giftCode) {
    const updated = await applyDiscountCode(giftCode, sections);
    if (updated?.sections) renderedSections = updated.sections;
  }

  const cart = await fetchCart();
  document.dispatchEvent(
    new CartAddEvent(cart, 'mucaris-cart-upsell', {
      source: 'mucaris-cart-upsell',
      itemCount: cart.item_count,
      variantId: String(variantId),
      sections: renderedSections,
    })
  );
  return '';
}

/* ---------------------------------------------------------------------------------------------------------------
   Quick view
   --------------------------------------------------------------------------------------------------------------- */

/** @type {Map<string, string>} */
const quickViewCache = new Map();

/** @type {{ gift: boolean, giftProperty: string, giftCode: string, opener: HTMLElement | null }} */
let quickViewState = { gift: false, giftProperty: '', giftCode: '', opener: null };

/**
 * The quick view <dialog> lives on <body>, outside the cart drawer, so re-rendering the cart never touches it and
 * opening it as a modal stacks it above the drawer.
 * @param {string} colorScheme
 */
function quickViewDialog(colorScheme) {
  let dialog = /** @type {HTMLDialogElement | null} */ (document.getElementById('MucarisQuickView'));
  if (!dialog) {
    dialog = document.createElement('dialog');
    dialog.id = 'MucarisQuickView';
    dialog.addEventListener('click', onQuickViewClick);
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeQuickView();
    });
    document.body.append(dialog);
  }
  dialog.className = `mc-quick-view ${colorScheme}`.trim();
  return dialog;
}

/**
 * @param {string} url
 * @param {{ gift: boolean, giftProperty: string, giftCode: string, colorScheme: string, opener: HTMLElement }} options
 */
async function openQuickView(url, options) {
  const sectionUrl = new URL(url, window.location.origin);
  sectionUrl.searchParams.set('section_id', QUICK_VIEW_SECTION);
  const key = sectionUrl.toString();

  let html = quickViewCache.get(key);
  if (!html) {
    const response = await fetch(key);
    if (!response.ok) throw new Error(`Quick view request failed (${response.status})`);
    html = await response.text();
    quickViewCache.set(key, html);
  }

  const dialog = quickViewDialog(options.colorScheme);
  dialog.innerHTML = html;
  quickViewState = { gift: options.gift, giftProperty: options.giftProperty, giftCode: options.giftCode, opener: options.opener };

  const root = dialog.querySelector('[data-qv]');
  if (!(root instanceof HTMLElement)) throw new Error('Quick view content missing');
  root.classList.toggle('is-gift', options.gift);
  if (options.gift) {
    dialog.querySelector('[data-qv-free]')?.removeAttribute('hidden');
    const compare = dialog.querySelector('[data-qv-compare]');
    if (compare instanceof HTMLElement) compare.hidden = true;
  }
  updateAddButton(root);

  dialog.classList.remove('is-closing');
  if (!dialog.open) {
    // Lock the page scroll in place (same as the cart drawer, see assets/dialog.js) – the page never moves
    document.documentElement.setAttribute('mc-quick-view-open', '');
    dialog.showModal();
  }
}

function closeQuickView() {
  const dialog = /** @type {HTMLDialogElement | null} */ (document.getElementById('MucarisQuickView'));
  if (!dialog || !dialog.open || dialog.classList.contains('is-closing')) return;

  const finish = () => {
    dialog.classList.remove('is-closing');
    dialog.close();
    document.documentElement.removeAttribute('mc-quick-view-open');
    const { opener } = quickViewState;
    if (opener?.isConnected) opener.focus({ preventScroll: true });
  };

  if (reducedMotion()) return finish();
  dialog.classList.add('is-closing');
  let done = false;
  const once = () => {
    if (done) return;
    done = true;
    finish();
  };
  dialog.addEventListener('animationend', once, { once: true });
  window.setTimeout(once, 400);
}

/** Add button: "Select size" until a size is picked, then "Add to cart" / "Add for free". */
function updateAddButton(root) {
  const button = root.querySelector('[data-qv-add]');
  const label = root.querySelector('[data-qv-add-label]');
  if (!(button instanceof HTMLButtonElement) || !label) return;

  const hasVariant = Boolean(button.dataset.variantId);
  // Nothing left to pick: keep the "Sold out" state from Liquid
  if (!hasVariant && root.querySelector('[data-qv-variant]:not([disabled])') === null) return;

  button.disabled = !hasVariant;
  if (!hasVariant) {
    label.textContent = root.dataset.chooseLabel || '';
  } else {
    label.textContent = (root.classList.contains('is-gift') ? root.dataset.giftLabel : root.dataset.addLabel) || '';
  }
}

/** @param {MouseEvent} event */
function onQuickViewClick(event) {
  const dialog = event.currentTarget;
  const target = event.target instanceof Element ? event.target : null;
  if (!target || !(dialog instanceof HTMLDialogElement)) return;

  // A click on the backdrop lands on the <dialog> itself
  if (target === dialog || target.closest('[data-qv-close]')) {
    closeQuickView();
    return;
  }

  const root = dialog.querySelector('[data-qv]');
  if (!(root instanceof HTMLElement)) return;

  const pill = target.closest('[data-qv-variant]');
  if (pill instanceof HTMLButtonElement && !pill.disabled) {
    selectVariant(root, pill);
    return;
  }

  const add = target.closest('[data-qv-add]');
  if (add instanceof HTMLButtonElement && !add.disabled) onQuickViewAdd(root, add);
}

/**
 * @param {HTMLElement} root
 * @param {HTMLButtonElement} pill
 */
function selectVariant(root, pill) {
  for (const other of root.querySelectorAll('[data-qv-variant]')) {
    other.classList.toggle('is-selected', other === pill);
    other.setAttribute('aria-pressed', String(other === pill));
  }

  const selected = root.querySelector('[data-qv-selected]');
  if (selected) selected.textContent = ` ${pill.dataset.title || ''}`;

  const price = root.querySelector('[data-qv-price]');
  if (price && pill.dataset.price) price.textContent = pill.dataset.price;

  const compare = root.querySelector('[data-qv-compare]');
  if (compare instanceof HTMLElement && !root.classList.contains('is-gift')) {
    compare.textContent = pill.dataset.compare || '';
    compare.hidden = !pill.dataset.compare;
  }

  const add = root.querySelector('[data-qv-add]');
  if (add instanceof HTMLButtonElement) add.dataset.variantId = pill.dataset.qvVariant || '';

  const error = root.querySelector('[data-qv-error]');
  if (error instanceof HTMLElement) error.hidden = true;

  updateAddButton(root);
}

/**
 * @param {HTMLElement} root
 * @param {HTMLButtonElement} button
 */
async function onQuickViewAdd(root, button) {
  const variantId = Number(button.dataset.variantId);
  if (!variantId) return;

  const error = root.querySelector('[data-qv-error]');
  setLoading(button, true);
  if (error instanceof HTMLElement) error.hidden = true;

  try {
    const message = await addToCart({
      variantId,
      gift: quickViewState.gift,
      giftProperty: quickViewState.giftProperty,
      giftCode: quickViewState.giftCode,
    });

    if (message) {
      if (error instanceof HTMLElement) {
        error.textContent = message;
        error.hidden = false;
      }
      setLoading(button, false);
      return;
    }

    closeQuickView();
    window.setTimeout(() => setLoading(button, false), 400);
  } catch (caught) {
    console.error('[MUCARIS cart] Adding the product failed:', caught);
    setLoading(button, false);
  }
}

/**
 * @param {HTMLButtonElement} button
 * @param {boolean} loading
 */
function setLoading(button, loading) {
  button.classList.toggle('is-loading', loading);
  button.disabled = loading;
  button.setAttribute('aria-busy', String(loading));
}

/* ---------------------------------------------------------------------------------------------------------------
   Upsell row
   --------------------------------------------------------------------------------------------------------------- */

/** Only one gift removal at a time; the same line key is never removed twice in a row. */
let giftRemoving = false;
let lastRemovedKey = '';

class MucarisCartUpsell extends HTMLElement {
  static get observedAttributes() {
    return ['data-gift-remove'];
  }

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#enforceGift();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#enforceGift();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const open = target.closest('[data-upsell-open]');
    if (open instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#openQuickView(open);
      return;
    }

    const step = target.closest('[data-upsell-step]');
    if (step instanceof HTMLElement) {
      const list = this.querySelector('[data-upsell-list]');
      const item = list?.querySelector('[data-upsell-item]');
      if (!list || !item) return;
      const distance = item.getBoundingClientRect().width + 8;
      list.scrollBy({ left: distance * Number(step.dataset.upsellStep), behavior: reducedMotion() ? 'auto' : 'smooth' });
    }
  };

  /** @param {HTMLButtonElement} button */
  async #openQuickView(button) {
    if (button.classList.contains('is-loading')) return;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    try {
      await openQuickView(button.dataset.upsellOpen || '', {
        gift: button.hasAttribute('data-gift'),
        giftProperty: this.dataset.giftProperty || 'Free gift',
        giftCode: (this.dataset.giftCode || '').trim(),
        colorScheme: this.dataset.colorScheme || '',
        opener: button,
      });
    } catch (error) {
      console.error('[MUCARIS cart] Opening the quick view failed:', error);
      // Fall back to the product page
      window.location.href = button.dataset.upsellOpen || '/';
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  }

  /** Removes the free gift line when the cart no longer qualifies (Liquid sets data-gift-remove). */
  async #enforceGift() {
    const key = this.dataset.giftRemove || '';
    if (!key) {
      lastRemovedKey = '';
      return;
    }
    if (giftRemoving || key === lastRemovedKey) return;

    giftRemoving = true;
    lastRemovedKey = key;

    try {
      const cart = await postJSON(Theme.routes.cart_change_url, {
        id: key,
        quantity: 0,
        sections: cartSectionIds().join(','),
        sections_url: window.location.pathname,
      });

      if (cart.status) throw new Error(cart.description || cart.message);

      const sections = cart.sections;
      delete cart.sections;
      document.dispatchEvent(
        new CartUpdateEvent(cart, 'mucaris-cart-gift', {
          source: 'mucaris-cart-gift',
          itemCount: cart.item_count,
          sections,
        })
      );
    } catch (error) {
      console.error('[MUCARIS cart] Removing the free gift failed:', error);
    } finally {
      giftRemoving = false;
    }
  }
}

if (!customElements.get('mucaris-cart-upsell')) {
  customElements.define('mucaris-cart-upsell', MucarisCartUpsell);
}
